import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { orderDisplayNumber } from '../utils/orderDisplay';
import { useCart } from '../context/CartContext';
import NotificationBell from './NotificationBell';

// Константы статусов заказа
const ORDER_STATUSES = {
  pending: { label: 'Создан', color: '#ffa500', icon: '⏳', step: 0 },
  awaiting_confirmation: { label: 'Требуется изменение', color: '#ff9800', icon: '✏️', step: 0 },
  confirmed: { label: 'Подтверждён', color: '#9c27b0', icon: '✅', step: 1 },
  paid: { label: 'Оплачен', color: '#1fb9c4', icon: '💳', step: 2 },
  preparing: { label: 'Готовится', color: '#2196f3', icon: '☕', step: 3 },
  ready: { label: 'Готов', color: '#4caf50', icon: '🎉', step: 4 },
  completed: { label: 'Выдан', color: '#9e9e9e', icon: '✅', step: 5 },
  cancelled: { label: 'Отменен', color: '#f44336', icon: '❌', step: -1 }
};

const OrderStatusView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { addItem, clearCafe, setSelectedCafe } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coffeeShop, setCoffeeShop] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);

  // Загрузка заказа при монтировании компонента
  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        const orderData = await apiService.getOrder(orderId);
        setOrder(orderData);

        // Загружаем информацию о кофейне
        if (orderData.coffee_shop_id) {
          try {
            const { prefetchCoffeeShops } = await import('../utils/coffeeShopsCache');
            const shops = await prefetchCoffeeShops();
            const shop = shops.find(s => s.id === orderData.coffee_shop_id);
            if (shop) {
              setCoffeeShop(shop);
            }
          } catch (err) {
            console.error('Error loading coffee shop:', err);
          }
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading order:', err);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  // Автоматическое обновление статуса заказа каждые 10 секунд
  useEffect(() => {
    if (!order || order.status === 'completed' || order.status === 'cancelled') {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const updatedOrder = await apiService.getOrder(orderId);
        setOrder(updatedOrder);
      } catch (err) {
        console.error('Error updating order:', err);
      }
    }, 10000); // Обновляем каждые 10 секунд

    return () => clearInterval(interval);
  }, [orderId, order]);

  // Автоматический переход в preparing после оплаты (через несколько секунд)
  useEffect(() => {
    if (!order || order.status !== 'paid') {
      return;
    }

    // Проверяем статус через 5 секунд после обнаружения статуса paid
    const timer = setTimeout(async () => {
      try {
        const updatedOrder = await apiService.getOrder(orderId);
        // Если статус все еще paid, возможно backend еще не обновил, но мы уже проверили
        // Если backend обновил в preparing, мы получим актуальный статус
        if (updatedOrder.status === 'paid') {
          // Если через 5 секунд все еще paid, проверяем еще раз через polling
          // Автоматический переход должен быть на backend, но для надежности проверяем
        }
        setOrder(updatedOrder);
      } catch (err) {
        console.error('Error checking order status after payment:', err);
      }
    }, 5000); // Проверяем через 5 секунд

    return () => clearTimeout(timer);
  }, [orderId, order?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Функция для загрузки заказа в корзину для редактирования
  const handleEditOrder = async () => {
    if (!order || !coffeeShop) return;

    try {
      setIsLoadingMenu(true);
      
      // Сохраняем текущее состояние корзины перед загрузкой заказа
      // Это нужно для восстановления корзины при выходе из режима редактирования
      const currentCartState = JSON.parse(localStorage.getItem('quickly_coffee_cart') || '{}');
      if (currentCartState && Object.keys(currentCartState.carts || {}).length > 0) {
        sessionStorage.setItem('quickly_coffee_cart_backup', JSON.stringify(currentCartState));
        if (process.env.NODE_ENV === 'development') {
          console.log('Saved cart backup before entering edit mode');
        }
      }
      
      // Загружаем меню кофейни
      const menuItems = await apiService.getMenuItems(order.coffee_shop_id);
      
      // Очищаем корзину для этой кофейни
      clearCafe(order.coffee_shop_id);
      
      // Устанавливаем выбранную кофейню
      setSelectedCafe(coffeeShop);
      
      // Загружаем товары заказа в корзину
      for (const orderItem of order.items) {
        const menuItem = menuItems.find(item => item.id === orderItem.menu_item_id);
        
        if (menuItem) {
          // Отладка: выводим структуру данных товара заказа
          if (process.env.NODE_ENV === 'development') {
            console.log('Loading order item to cart:', {
              menu_item_id: orderItem.menu_item_id,
              item_name: orderItem.item_name || menuItem.name,
              addon_ids: orderItem.addon_ids,
              addons: orderItem.addons,
              size_id: orderItem.size_id
            });
          }
          
          // Находим размер, если он был выбран
          let selectedSize = null;
          if (orderItem.size_id) {
            selectedSize = menuItem.sizes?.find(s => s.id === orderItem.size_id);
            if (!selectedSize && process.env.NODE_ENV === 'development') {
              console.warn(`Size ${orderItem.size_id} not found in menu for item ${menuItem.name}`);
            }
          }
          
          // Находим допники, если они были выбраны
          // Формат данных от бэкенда: orderItem.addons - массив объектов с полями:
          // { id: 29, addon_id: 9, addon_name: "...", price_at_time: 40 }
          const selectedAddons = [];
          
          // Обрабатываем addons из заказа (приоритетный источник)
          if (orderItem.addons && Array.isArray(orderItem.addons) && orderItem.addons.length > 0) {
            orderItem.addons.forEach(addonObj => {
              if (addonObj && addonObj.addon_id != null) {
                // Используем addon_id для поиска аддона в актуальном меню
                const addonId = addonObj.addon_id;
                let addon = menuItem.addons?.find(a => a.id === addonId);
                
                // Если аддон найден в меню, используем данные из меню
                if (addon) {
                  selectedAddons.push(addon);
                } else {
                  // Если аддон не найден в меню (удален или изменен), используем данные из заказа
                  // Но преобразуем в формат, который ожидается в корзине
                  addon = {
                    id: addonId,
                    name: addonObj.addon_name || 'Допник',
                    price: addonObj.price_at_time || 0,
                    volume_weight: null, // В заказе может не быть этой информации
                  };
                  selectedAddons.push(addon);
                  
                  if (process.env.NODE_ENV === 'development') {
                    console.warn(`Addon ${addonId} (${addonObj.addon_name}) not found in current menu, using order data`);
                  }
                }
              }
            });
          }
          
          // Альтернативный вариант: если addon_ids есть, но addons нет (для обратной совместимости)
          if (selectedAddons.length === 0 && orderItem.addon_ids && Array.isArray(orderItem.addon_ids)) {
            orderItem.addon_ids.forEach(addonIdOrObj => {
              const addonId = typeof addonIdOrObj === 'object' && addonIdOrObj !== null 
                ? addonIdOrObj.id || addonIdOrObj.addon_id 
                : addonIdOrObj;
              
              if (addonId != null && addonId !== undefined) {
                const addon = menuItem.addons?.find(a => a.id === addonId);
                if (addon) {
                  selectedAddons.push(addon);
                } else if (process.env.NODE_ENV === 'development') {
                  console.warn(`Addon ${addonId} not found in menu for item ${menuItem.name}`);
                }
              }
            });
          }
          
          // Отладка: выводим найденные аддоны
          if (process.env.NODE_ENV === 'development') {
            console.log('Selected addons for item:', {
              item_name: menuItem.name,
              orderItem_addons: orderItem.addons,
              addons_count: selectedAddons.length,
              addons: selectedAddons.map(a => ({ id: a.id, name: a.name, price: a.price }))
            });
          }
          
          // Рассчитываем цену
          const basePrice = selectedSize ? Number(selectedSize.price) : Number(menuItem.price);
          const addonsPrice = selectedAddons.reduce((sum, addon) => sum + Number(addon.price || 0), 0);
          const totalPrice = basePrice + addonsPrice;
          
          // Формируем аддоны в правильном формате для корзины
          // Используем данные из меню (если аддон найден) или из заказа (если не найден)
          const formattedAddons = selectedAddons.map(a => ({
            id: a.id,
            name: a.name || 'Допник',
            price: a.price || 0,
            volume_weight: a.volume_weight || null,
          }));
          
          // Формируем товар для корзины
          const cartItem = {
            ...menuItem,
            quantity: orderItem.quantity,
            price: totalPrice,
            size: selectedSize?.size_name || null,
            size_id: selectedSize?.id || null,
            customizations: {
              sizeId: selectedSize?.id || null,
              sizeName: selectedSize?.size_name || null,
              sizeVolumeWeight: selectedSize?.volume_weight || null,
              addons: formattedAddons,
            },
            // Сохраняем addon_ids для обратной совместимости
            addon_ids: formattedAddons.map(a => a.id),
          };
          
          // Отладка: проверяем сформированный товар для корзины
          if (process.env.NODE_ENV === 'development') {
            console.log('Cart item created:', {
              name: cartItem.name,
              customizations: cartItem.customizations,
              customizations_addons: cartItem.customizations.addons,
              customizations_addons_length: cartItem.customizations.addons.length,
              addon_ids: cartItem.addon_ids
            });
          }
          
          addItem(coffeeShop, cartItem);
        }
      }
      
      // Переходим в корзину с флагом редактирования
      navigate(`/cart?editOrderId=${order.id}`);
    } catch (error) {
      console.error('Error loading order for editing:', error);
      alert(`Ошибка при загрузке заказа для редактирования: ${error.message}`);
    } finally {
      setIsLoadingMenu(false);
    }
  };

  const handleBackToOrders = () => {
    navigate('/orders');
  };

  const handleBackToMap = () => {
    navigate('/');
  };

  const handlePayment = () => {
    if (order?.payment_url) {
      // Открываем ссылку на оплату в новом окне
      window.open(order.payment_url, '_blank');
    } else {
      alert('Ссылка на оплату недоступна. Пожалуйста, обратитесь в поддержку.');
    }
  };

  const handleCancelButtonClick = () => {
    if (!order) return;
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    if (cancellingOrderId) return;
    setIsCancelModalOpen(false);
  };

  const handleCancelOrder = async () => {
    if (!order) {
      return;
    }

    try {
      setCancellingOrderId(order.id);
      await apiService.cancelOrder(order.id);
      // Обновляем заказ после отмены
      const updatedOrder = await apiService.getOrder(order.id);
      setOrder(updatedOrder);
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert(`Ошибка при отмене заказа: ${error.message}`);
    } finally {
      setCancellingOrderId(null);
      setIsCancelModalOpen(false);
    }
  };

  // Функция для расчета прогресса
  const getProgress = () => {
    if (!order) return 0;
    const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
    if (status.step < 0) return 0;
    
    // Маппинг статусов для прогресса: pending(0) -> confirmed(1) -> paid(2) -> preparing(3) -> ready(4)
    // Прогресс-бар показывает 4 шага: 0%, 25%, 50%, 75%, 100%
    const progressMap = {
      0: 0,   // pending / awaiting_confirmation
      1: 1,   // confirmed
      2: 2,   // paid
      3: 3,   // preparing
      4: 4,   // ready
      5: 4    // completed (максимальный прогресс)
    };
    
    const mappedStep = progressMap[status.step] ?? 0;
    return (mappedStep / 4) * 100;
  };

  // Функция для получения времени до готовности
  const getTimeRemaining = () => {
    if (!order) return null;
    
    // Если заказ готов, показываем соответствующее сообщение
    if (order.status === 'ready') {
      return 'Готов к выдаче';
    }
    
    if (!order.estimated_ready_time) return null;
    
    // Парсим дату с учетом того, что она приходит в UTC из базы
    const readyTime = parseDateFromAPI(order.estimated_ready_time);
    if (!readyTime || isNaN(readyTime.getTime())) return null;
    
    const now = new Date();
    const diff = readyTime - now;
    
    if (diff <= 0) return 'Скоро будет готов';
    
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) {
      return `Осталось ~${minutes} мин`;
    }
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `Осталось ~${hours}ч ${mins}мин`;
  };

  // Вспомогательная функция для правильного парсинга даты из API
  const parseDateFromAPI = (dateString) => {
    if (!dateString) return null;
    
    // Если строка уже содержит 'Z' или '+', значит часовой пояс указан
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
      return new Date(dateString);
    }
    
    // Если часовой пояс не указан, предполагаем, что это UTC и добавляем 'Z'
    // Это важно, так как сервер хранит время в UTC
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString);
  };

  // Форматирование даты и времени (использует локальное время пользователя)
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    // Парсим дату с учетом того, что она приходит в UTC из базы
    const date = parseDateFromAPI(dateString);
    if (!date || isNaN(date.getTime())) return '';
    
    // Получаем локальный часовой пояс браузера
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Используем локальное время браузера
    const timeStr = date.toLocaleTimeString('ru-RU', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: timeZone
    });
    const dateStr = date.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'long',
      timeZone: timeZone
    });
    return `${timeStr}, ${dateStr}`;
  };

  // Состояние загрузки
  if (loading) {
    return (
      <div className="order-status-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>Заказ #{orderId}</h1>
              <p className="subtitle">Загрузка...</p>
            </div>
            <button 
              className="btn btn-secondary"
              onClick={handleBackToOrders}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Назад
            </button>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          color: 'white',
          fontSize: '18px'
        }}>
          <div>Загружаем информацию о заказе...</div>
        </div>
      </div>
    );
  }

  // Состояние ошибки
  if (error) {
    return (
      <div className="order-status-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>Заказ #{orderId}</h1>
              <p className="subtitle">Ошибка загрузки</p>
            </div>
            <button 
              className="btn btn-secondary"
              onClick={handleBackToOrders}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Назад
            </button>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ marginBottom: '16px' }}>{error}</div>
          <button 
            className="btn"
            onClick={() => window.location.reload()}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  // Проверяем, можно ли редактировать заказ
  const canEditOrder = order.status === 'pending' || order.status === 'awaiting_confirmation';
  const isAwaitingConfirmation = order.status === 'awaiting_confirmation';
  
  // Для статуса awaiting_confirmation показываем реальный статус с предупреждением
  const displayStatus = order.status;
  const currentStatus = ORDER_STATUSES[displayStatus] || ORDER_STATUSES.pending;
  const progress = getProgress();
  const timeRemaining = getTimeRemaining();

  return (
    <div className="order-status-container">
      <div className="header">
        <div className="flex space-between align-center">
          <div>
            <h1>Заказ #{orderDisplayNumber(order)}</h1>
            <p className="subtitle">{formatDateTime(order.created_at)}</p>
          </div>
          <div className="flex gap-2 header-right-actions">
            <NotificationBell />
            <button 
              className="btn btn-secondary"
              onClick={handleBackToMap}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Карта
            </button>
            <button 
              className="btn btn-secondary"
              onClick={handleBackToOrders}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              История
            </button>
          </div>
        </div>
      </div>

      <div className="order-status-content">
        {/* Статус заказа с прогресс-баром */}
        <div className="order-status-card">
          <div className="status-badge" style={{ backgroundColor: currentStatus.color }}>
            <span className="status-icon">{currentStatus.icon}</span>
            <span className="status-label">{currentStatus.label}</span>
          </div>

          {order.status !== 'cancelled' && (
            <div className="progress-tracker">
              <div className="progress-bar-container">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${progress}%`, backgroundColor: currentStatus.color }}
                />
              </div>
              <div className="progress-steps">
                {['pending', 'confirmed', 'paid', 'preparing', 'ready'].map((status, index) => {
                  const statusInfo = ORDER_STATUSES[status];
                  if (!statusInfo) return null;
                  
                  // Определяем активность шага: текущий статус должен иметь step >= step этого шага
                  const currentStep = currentStatus.step;
                  const stepStep = statusInfo.step;
                  const isActive = currentStep >= stepStep;
                  
                  return (
                    <div 
                      key={status} 
                      className={`progress-step ${isActive ? 'active' : ''}`}
                      style={{ color: isActive ? statusInfo.color : '#ccc' }}
                    >
                      <div className="step-icon">{statusInfo.icon}</div>
                      <div className="step-label">{statusInfo.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Информация о кофейне */}
        {coffeeShop && (
          <div className="order-info-card">
            <h3>Кофейня</h3>
            <p className="cafe-name">{coffeeShop.name}</p>
            <p className="cafe-address">{coffeeShop.address}</p>
            {timeRemaining && order.status !== 'completed' && order.status !== 'cancelled' && (
              <p className="time-remaining">
                {timeRemaining}
              </p>
            )}
            {order.estimated_ready_time && (
              <p className="ready-time">
                Готовность: {formatDateTime(order.estimated_ready_time)}
              </p>
            )}
          </div>
        )}

        {/* Время готовности/отмены заказа */}
        {(order.status === 'ready' || order.status === 'completed' || order.status === 'cancelled') && order.updated_at && (
          <div className="order-info-card">
            <h3>
              {order.status === 'ready' ? 'Заказ готов' : order.status === 'completed' ? 'Заказ выдан' : 'Заказ отменен'}
            </h3>
            <p className="ready-time" style={{ fontSize: '16px', marginTop: '8px' }}>
              {order.status === 'ready' ? 'Готов к выдаче' : order.status === 'completed' ? 'Выдан' : 'Отменен'}: {formatDateTime(order.updated_at)}
            </p>
          </div>
        )}

        {/* Уведомление о необходимости редактирования (для AWAITING_CONFIRMATION) */}
        {isAwaitingConfirmation && (
          <div className="order-info-card" style={{
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 20px rgba(255, 152, 0, 0.4)',
            animation: 'pulse 2s infinite'
          }}>
            <h3 style={{ 
              color: 'white', 
              marginBottom: '12px',
              fontSize: '20px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Требуется изменение заказа
            </h3>
            <p style={{ 
              margin: '0 0 16px 0', 
              lineHeight: 1.6,
              fontSize: '16px',
              fontWeight: '500'
            }}>
              Некоторые позиции заказа требуют изменения. Пожалуйста, отредактируйте заказ и сохраните изменения.
            </p>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Нажмите кнопку "Редактировать заказ" ниже, чтобы внести изменения
            </div>
          </div>
        )}

        {/* Состав заказа */}
        {order.items && order.items.length > 0 && (
          <div className="order-info-card">
            <h3>Состав заказа</h3>
            <div className="order-items-list">
              {order.items.map((item, index) => (
                <div key={index} className="order-item-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="item-info">
                    <span className="item-name">{item.item_name || item.menu_item?.name || `Товар #${item.menu_item_id}`}</span>
                    <span className="item-quantity">× {item.quantity}</span>
                  </div>
                  <div className="item-price">
                    {item.price_at_time ? `${(item.price_at_time * item.quantity).toFixed(2)} ₽` : '—'}
                  </div>
                  </div>
                  
                  {/* Отображение размера, если есть */}
                  {item.size_name && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#666',
                      marginLeft: '8px',
                      fontStyle: 'italic'
                    }}>
                      Объем: {item.size_name}
                    </div>
                  )}
                  
                  {/* Отображение допников, если есть */}
                  {item.addons && item.addons.length > 0 && (
                    <div style={{ 
                      marginLeft: '8px',
                      paddingLeft: '12px',
                      borderLeft: '2px solid #e0e0e0'
                    }}>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#888', 
                        marginBottom: '4px',
                        fontWeight: '500'
                      }}>
                        Дополнительно:
                      </div>
                      {item.addons.map((addon, addonIndex) => (
                        <div key={addonIndex} style={{ 
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#4caf50' }}>+</span>
                            <span>{addon.addon_name || addon.name || 'Допник'}</span>
                          </div>
                          <span style={{ color: '#888' }}>
                            +{Number(addon.price_at_time || addon.price || 0).toFixed(2)} ₽
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(order.discount_amount != null && Number(order.discount_amount) > 0) && (
                <div className="order-item-row" style={{ borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '8px' }}>
                  <div className="item-info">
                    <span className="item-name">Скидка (промокод)</span>
                  </div>
                  <div className="item-price" style={{ color: '#2e7d32' }}>
                    −{Number(order.discount_amount).toFixed(2)} ₽
                  </div>
                </div>
              )}
              {(order.loyalty_discount_amount != null && Number(order.loyalty_discount_amount) > 0) && (
                <div className="order-item-row" style={{ borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '8px' }}>
                  <div className="item-info">
                    <span className="item-name">
                      Скидка (лояльность
                      {order.loyalty_level_at_order != null
                        ? `, ур. ${order.loyalty_level_at_order}`
                        : ''}
                      )
                    </span>
                  </div>
                  <div className="item-price" style={{ color: '#2e7d32' }}>
                    −{Number(order.loyalty_discount_amount).toFixed(2)} ₽
                  </div>
                </div>
              )}
              {(order.loyalty_bonus_spent != null && Number(order.loyalty_bonus_spent) > 0) && (
                <div className="order-item-row" style={{ borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '8px' }}>
                  <div className="item-info">
                    <span className="item-name">Списание баллов</span>
                  </div>
                  <div className="item-price" style={{ color: '#2e7d32' }}>
                    −{Number(order.loyalty_bonus_spent).toFixed(2)} ₽
                  </div>
                </div>
              )}
              {(() => {
                const serviceFee = order.service_commission ?? 0; // комиссия из API (0 если не передана)
                
                return (
                  <div className="order-item-row" style={{ borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '8px' }}>
                    <div className="item-info">
                      <span className="item-name">Сервисный сбор</span>
                    </div>
                    <div className="item-price">
                      {Number(serviceFee).toFixed(2)} ₽
                    </div>
                  </div>
                );
              })()}
              <div className="order-total-row">
                <span className="total-label">Итого:</span>
                <span className="total-price">{order.total_amount ? Number(order.total_amount).toFixed(2) : 0} ₽</span>
              </div>
            </div>
          </div>
        )}


        {/* Кнопки действий - фиксированные внизу */}
        {(canEditOrder || order.status === 'confirmed' || order.status === 'completed' || order.status === 'cancelled') && (
          <div className="order-actions-container" style={(canEditOrder || order.status === 'confirmed') ? { flexDirection: 'column' } : {}}>
            {/* Кнопка оплаты для подтвержденных заказов */}
            {order.status === 'confirmed' && order.payment_url && (
              <button 
                className="order-payment-btn"
                onClick={handlePayment}
                style={{
                  width: '100%',
                  marginBottom: '12px'
                }}
              >
                Оплатить {order.total_amount ? Number(order.total_amount).toFixed(0) : 0} ₽
              </button>
            )}

            {/* Кнопка отмены для подтвержденных заказов */}
            {order.status === 'confirmed' && (
              <button 
                className="order-cancel-btn"
                onClick={handleCancelButtonClick}
                style={{
                  width: '100%',
                  marginBottom: '12px'
                }}
              >
                Отменить заказ
              </button>
            )}

            {/* Кнопки для редактируемых заказов (pending или awaiting_confirmation) */}
            {canEditOrder && (
              <>
                <div style={{ display: 'flex', gap: '12px', width: '100%', flexDirection: 'row-reverse' }}>
                  <button 
                    className="order-cancel-btn"
                    onClick={handleCancelButtonClick}
                  >
                    Отменить заказ
                  </button>
                </div>
                <button 
                  className="order-edit-btn"
                  onClick={handleEditOrder}
                  disabled={isLoadingMenu}
                  style={{
                    cursor: isLoadingMenu ? 'not-allowed' : 'pointer',
                    opacity: isLoadingMenu ? 0.6 : 1,
                    width: '100%',
                    marginTop: '12px'
                  }}
                >
                  {isLoadingMenu ? 'Загрузка...' : 'Редактировать заказ'}
                </button>
              </>
            )}

            {/* Кнопка реордера для завершённых и отменённых заказов */}
            {(order.status === 'completed' || order.status === 'cancelled') && (
              <button 
                className="order-new-order-btn"
                onClick={handleBackToMap}
              >
                Сделать новый заказ
              </button>
            )}
          </div>
        )}
      </div>

      {/* Модальное окно отмены заказа */}
      {isCancelModalOpen && order && (
        <div className="modal-overlay" onClick={handleCloseCancelModal}>
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '420px',
              padding: '28px',
              textAlign: 'center'
            }}
          >
            <h3 style={{ marginBottom: '16px', fontSize: '20px', color: 'white' }}>
              Отменить заказ #{orderDisplayNumber(order)}?
            </h3>
            <p style={{ marginBottom: '20px', color: 'white', lineHeight: 1.5 }}>
              После отмены вы не сможете восстановить этот заказ. Если уже была произведена оплата, обратитесь в поддержку.
            </p>
            <div style={{
              background: '#f8f9fa',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#333' }}>
                <strong>Кофейня:</strong> {coffeeShop?.name || '—'}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#666' }}>
                <strong>Создан:</strong> {formatDateTime(order.created_at)}
              </p>
              {(order.discount_amount != null && Number(order.discount_amount) > 0) && (
                <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#2e7d32' }}>
                  <strong>Скидка (промокод):</strong> −{Number(order.discount_amount).toFixed(2)} ₽
                </p>
              )}
              {(order.loyalty_discount_amount != null && Number(order.loyalty_discount_amount) > 0) && (
                <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#2e7d32' }}>
                  <strong>Скидка (лояльность):</strong> −{Number(order.loyalty_discount_amount).toFixed(2)} ₽
                </p>
              )}
              {(order.loyalty_bonus_spent != null && Number(order.loyalty_bonus_spent) > 0) && (
                <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#2e7d32' }}>
                  <strong>Списание баллов:</strong> −{Number(order.loyalty_bonus_spent).toFixed(2)} ₽
                </p>
              )}
              <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#666' }}>
                <strong>Сумма:</strong> {order.total_amount ? Number(order.total_amount).toFixed(2) : 0} ₽
              </p>
            </div>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button 
                className="btn btn-cancel" 
                onClick={handleCancelOrder}
                disabled={cancellingOrderId === order.id}
              >
                {cancellingOrderId === order.id ? 'Отменяем…' : 'Отменить заказ'}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handleCloseCancelModal}
                disabled={cancellingOrderId === order.id}
              >
                Оставить заказ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderStatusView;

